import { HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, EMPTY, Observable, of, Subject, Subscriber, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from '../../core/services/authentication.service';
import { OauthProvider } from '../../oauth/providers';
import { HubspotService, SalesforceService, DynamicsService, ZohoService } from './crm';
import { OutreachService } from './outreach.service';
import { SalesloftService } from './salesloft.service';
import { InboxintelService } from '../../results/inboxintel/services/inboxintel.service';
import { UserService } from '../../core/services/user.service';
import { AccountAttributesEnum, CrmAppEnum, FeatureFlagsEnum } from '../enums';
import { catchError, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { HubspotCrmPromptComponent } from '../components/hubspot-crm-prompt/hubspot-crm-prompt.component';
import { User } from 'src/app/core/models/user.model';
import { AccountService } from './account.service';
import { MatDialog } from '@angular/material/dialog';
import { IntentSettingsService } from 'src/app/modals/intent-settings/services/intent-settings.service';
import { HubspotApiService } from './api';
import { getErrorMessage } from '../helpers';

export interface OauthWindowStatusResult {
    success: boolean;
    provider: OauthProvider;
    selectedItems?: any;
}

@Injectable({
    providedIn: 'root'
})
export class OauthService {
    public saveSearchUpdated$: EventEmitter<any>;

    private authWindow: Window;
    private interval: number;
    private loopCount = 300;
    private authWindowStatusSubject: BehaviorSubject<OauthWindowStatusResult> = new BehaviorSubject({
        success: false,
        provider: null,
    });
    public readonly authWindowStatus$ = this.authWindowStatusSubject.asObservable();
    private exportContinueEvent = new Subject<void>();

    constructor(
        private salesforceService: SalesforceService,
        private hubspotService: HubspotService,
        private hubspotApiService: HubspotApiService,
        private salesloftService: SalesloftService,
        private outreachService: OutreachService,
        private dynamicsService: DynamicsService,
        private zohoService: ZohoService,
        private authService: AuthenticationService,
        private toastr: ToastrService,
        private inboxIntelService: InboxintelService,
        private readonly userService: UserService,
        private readonly matDialog: MatDialog,
        private readonly accountService: AccountService,
        private intentSettingsService: IntentSettingsService,
    ) {
        this.saveSearchUpdated$ = new EventEmitter();
    }

    public saveSearchUpdate(item: any) {
        this.saveSearchUpdated$.next(item);
    }

    private authenticate(oauthProvider: string, params: HttpParams, hsCrmValue?: boolean): Observable<any> {
        const code: string = params.get('code');

        switch (oauthProvider) {
            case OauthProvider.Salesforce:
            case OauthProvider.SalesforceCompany:
                return this.salesforceService.authenticate(code);

            case OauthProvider.Hubspot:
            case OauthProvider.HubspotLite:
                return this.hubspotService.authenticate(code, hsCrmValue);

            case OauthProvider.HubspotFilters:
                return this.hubspotApiService.authenticateFilters({ code, redirect_uri: environment.hubspotFilters.redirectUrl });

            case OauthProvider.HubspotBceEnrichment:
                return this.hubspotApiService.authenticateEnrichment({
                    code,
                    crmApp: CrmAppEnum.ENRICH_BCE,
                    redirect_uri: environment.hubspotBceEnrichment.redirectUrl,
                });

            case OauthProvider.HubspotEnrichment:
                return this.hubspotApiService.authenticateEnrichment({
                    code,
                    crmApp: CrmAppEnum.ENRICH,
                    redirect_uri: environment.hubspotEnrichment.redirectUrl,
                });

            case OauthProvider.SalesLoft:
                return this.salesloftService.authenticate(code);

            case OauthProvider.Outreach:
                return this.outreachService.authenticate(code);

            case OauthProvider.Dynamics:
            case OauthProvider.DynamicsCompany:
                return this.dynamicsService.authenticate(code);

            case OauthProvider.Zoho:
            case OauthProvider.ZohoCompany:
                const accountsServer: string = params.get('accounts-server');
                return this.zohoService.authenticate(code, accountsServer);

            case OauthProvider.Google:
                return this.inboxIntelService.authenticate(code, 'GOOGLE');

            case OauthProvider.Microsoft:
                return this.inboxIntelService.authenticate(code, 'MICROSOFT');
        }
    }

    private settings(provider: OauthProvider): any {
        switch (provider) {
            case OauthProvider.Salesforce:
            case OauthProvider.SalesforceCompany:
                const salesforceOauthSettings: HttpParams = new HttpParams({
                    fromObject: {
                        client_id: environment.salesforce.clientId,
                        redirect_uri: environment.salesforce.redirectUrl,
                        response_type: 'code',
                        prompt: 'login consent',
                    }
                });
                return salesforceOauthSettings.toString();

            case OauthProvider.Hubspot:
                const hubspotOauthSettings: HttpParams = new HttpParams({
                    fromObject: {
                        client_id: environment.hubspot.clientId,
                        redirect_uri: environment.hubspot.redirectUrl,
                        response_type: 'code',
                        scope: 'crm.objects.contacts.read crm.objects.contacts.write crm.schemas.contacts.read crm.objects.companies.read crm.objects.owners.read',
                        optional_scope: 'automation crm.lists.read crm.schemas.contacts.write crm.schemas.companies.read crm.objects.companies.write'
                    }
                });
                return hubspotOauthSettings.toString();

            case OauthProvider.HubspotLite:
                const hubspotLiteOauthSettings: HttpParams = new HttpParams({
                    fromObject: {
                        client_id: environment.hubspot.clientId,
                        redirect_uri: environment.hubspot.redirectUrl,
                        response_type: 'code',
                        scope: 'crm.objects.contacts.read crm.objects.contacts.write crm.schemas.contacts.read crm.objects.companies.read crm.objects.owners.read',
                        optional_scope: 'automation crm.lists.read crm.schemas.contacts.write crm.schemas.companies.read crm.objects.companies.write'
                    }
                });
                return hubspotLiteOauthSettings.toString();

            case OauthProvider.HubspotFilters:
                const hubspotFiltersOauthSettings: HttpParams = new HttpParams({
                    fromObject: {
                        client_id: environment.hubspotFilters.clientId,
                        redirect_uri: environment.hubspotFilters.redirectUrl,
                        response_type: 'code',
                        scope: 'crm.objects.owners.read crm.schemas.companies.read crm.schemas.contacts.read crm.export crm.objects.contacts.read crm.objects.companies.read',
                    }
                });
                return hubspotFiltersOauthSettings.toString();

            case OauthProvider.HubspotBceEnrichment:
                const hubspotBceEnrichOauthSettings: HttpParams = new HttpParams({
                    fromObject: {
                        client_id: environment.hubspotBceEnrichment.clientId,
                        redirect_uri: environment.hubspotBceEnrichment.redirectUrl,
                        response_type: 'code',
                        scope: 'crm.schemas.contacts.write crm.schemas.deals.read crm.schemas.deals.write oauth crm.objects.owners.read crm.objects.contacts.write crm.objects.companies.read crm.lists.read crm.objects.deals.read crm.schemas.contacts.read crm.objects.deals.write crm.objects.contacts.read',
                    }
                });
                return hubspotBceEnrichOauthSettings.toString();

            case OauthProvider.HubspotEnrichment:
                const hubspotEnrichOauthSettings: HttpParams = new HttpParams({
                    fromObject: {
                        client_id: environment.hubspotEnrichment.clientId,
                        redirect_uri: environment.hubspotEnrichment.redirectUrl,
                        response_type: 'code',
                        scope: 'crm.schemas.companies.write crm.schemas.contacts.write crm.import oauth crm.objects.owners.read crm.export crm.objects.contacts.write crm.objects.companies.write crm.objects.companies.read crm.lists.read crm.schemas.contacts.read crm.objects.contacts.read crm.schemas.companies.read',
                    }
                });
                return hubspotEnrichOauthSettings.toString();

            case OauthProvider.SalesLoft:
                const salesloftOauthSettings: HttpParams = new HttpParams({
                    fromObject: {
                        client_id: environment.salesloft.clientId,
                        redirect_uri: environment.salesloft.redirectUrl,
                        response_type: 'code',
                    }
                });
                return salesloftOauthSettings.toString();

            case OauthProvider.Outreach:
                const outreachOauthSettings: HttpParams = new HttpParams({
                    fromObject: {
                        client_id: environment.outreach.clientId,
                        redirect_uri: environment.outreach.redirectUrl,
                        response_type: 'code',
                        scope: 'profile email prospects.read prospects.write mailboxes.read sequences.read sequenceStates.write accounts.read accounts.write users.read'
                    }
                });
                return outreachOauthSettings.toString();

            case OauthProvider.Dynamics:
            case OauthProvider.DynamicsCompany:
                const dynamicsOauthSettings: HttpParams = new HttpParams({
                    fromObject: {
                        client_id: environment.dynamics.clientId,
                        redirect_uri: environment.dynamics.redirectUrl,
                        response_type: 'code',
                        scope: 'offline_access' + this.authService.getDyAuthUrl() + '/.default'
                    }
                });
                return dynamicsOauthSettings.toString();

            case OauthProvider.Zoho:
            case OauthProvider.ZohoCompany:
                const zohoOauthSettings: HttpParams = new HttpParams({
                    fromObject: {
                        client_id: environment.zoho.clientId,
                        redirect_uri: environment.zoho.redirectUrl,
                        response_type: 'code',
                        scope: 'AAAserver.profile.Read,ZohoCRM.modules.ALL,ZohoCRM.users.READ,ZohoCRM.settings.fields.ALL',
                        access_type: 'offline'
                    }
                });
                return zohoOauthSettings.toString();

            case OauthProvider.Google:
                const googelOauthSettings: HttpParams = new HttpParams({
                    fromObject: {
                        client_id: environment.google.clientId,
                        redirect_uri: environment.google.redirectUrl,
                        response_type: 'code',
                        scope: 'openid profile email https://www.googleapis.com/auth/gmail.readonly',
                        prompt: 'consent',
                        access_type: 'offline',
                    }
                });
                return googelOauthSettings.toString();

            case OauthProvider.Microsoft:
                const microsoftOauthSettings: HttpParams = new HttpParams({
                    fromObject: {
                        client_id: environment.microsoft.clientId,
                        redirect_uri: environment.microsoft.redirectUrl,
                        response_type: 'code',
                        scope: 'User.Read Mail.Read offline_access',
                        prompt: 'consent',
                        access_type: 'offline',
                    }
                });
                return microsoftOauthSettings.toString();
        }
    }

    private url(provider: OauthProvider): string {

        switch (provider) {
            case OauthProvider.Salesforce:
            case OauthProvider.SalesforceCompany:
                return this.authService.getSfAuthUrl();

            case OauthProvider.Hubspot:
            case OauthProvider.HubspotLite:
                return environment.hubspot.authUrl;

            case OauthProvider.HubspotFilters:
                return environment.hubspotFilters.authUrl;

            case OauthProvider.HubspotBceEnrichment:
                return environment.hubspotBceEnrichment.authUrl;

            case OauthProvider.HubspotEnrichment:
                return environment.hubspotEnrichment.authUrl;

            case OauthProvider.SalesLoft:
                return environment.salesloft.authUrl;

            case OauthProvider.Outreach:
                return environment.outreach.authUrl;

            case OauthProvider.Dynamics:
            case OauthProvider.DynamicsCompany:
                return environment.dynamics.authUrl;

            case OauthProvider.Zoho:
            case OauthProvider.ZohoCompany:
                return environment.zoho.authUrl;

            case OauthProvider.Google:
                return environment.google.authUrl;

            case OauthProvider.Microsoft:
                return environment.microsoft.authUrl;
        }
    }

    private createWindow(url: string, name = 'Authorization', width = 400, height = 500, left = screen.width / 2 - 200, top = screen.height / 2 - 250): Window {
        const options = `width=${width},height=${height},left=${left},top=${top}`;
        return window.open(url, name, options);
    }

    private openFlow(provider, matchIds, fromList, hsCrmValue?: boolean) {
        // open the auth window
        const isProviderHubSpot = provider.toLowerCase().includes('hubspot');
        const width = 400;
        const height = isProviderHubSpot ? 580 : 500;
        const left = screen.width / 2 - 200;
        const top = isProviderHubSpot ? screen.height / 2 - 290 : screen.height / 2 - 250;
        this.authWindow = this.createWindow(this.url(provider) + '?' + this.settings(provider), provider, width, height, left, top);
        if (!this.authWindow) return;
        this.loopCount = 300;
        // set the interval up
        this.interval = window.setInterval(() => {
            // automatically close the window after x loops
            if (this.loopCount-- < 0) {
                window.clearInterval(this.interval);
                this.authWindow.close();
                this.authWindowStatusSubject.next({
                    success: false,
                    provider: provider,
                    selectedItems: null
                } as OauthWindowStatusResult);
            }

            if (this.authWindow.closed) {
                window.clearInterval(this.interval);
            } else {
                try {
                    const href = this.authWindow.location.href;
                    if (href) {
                        const params = new HttpParams({
                            fromString: href.split('?')[1]
                        });
                        if (params.has('code')) {
                            this.authenticate(this.authWindow.name, params, hsCrmValue).subscribe(r => {
                                window.clearInterval(this.interval);
                                if ((fromList === 'google' || fromList === 'microsoft') && r.response === 'Success') {
                                    this.inboxIntelService.enableAuthentication = false;
                                    this.inboxIntelService.scanInProgress = true;
                                    this.inboxIntelService.isExistingUser = false;
                                    this.inboxIntelService.scanGmailInbox();
                                } else {
                                    const isExportStart = (fromList === 'companyList' || fromList === 'list') ? true : false;
                                    // if (type === '' || type === 'settings' || type === 'reconnect') {
                                    this.authService.updateLoggedUserData().then(() => {
                                        this.authWindowStatusSubject.next({
                                            success: true,
                                            provider: provider,
                                            selectedItems: matchIds,
                                            isExportStart: isExportStart,
                                            fromList
                                        } as OauthWindowStatusResult);
                                    });
                                    if (provider === 'salesforce' && fromList === 'fromInboxIntel') {
                                        this.salesforceService.isFromInboxIntel = true;
                                    }
                                    if (provider === 'salesforce_company' && fromList === 'fromInboxIntelCompany') {
                                        this.salesforceService.isFromInboxIntelCompany = true;
                                    }
                                    this.intentSettingsService.CRMReconnectedSubject.next(provider);
                                }
                                // }

                                // this.authService.updateLoggedUserData().then(() => {
                                //     if (type !== 'companyExport') {
                                //         this.crmAdd({ 'provider': provider, 'matchIds': matchIds, 'type': type });
                                //     } else {
                                //         this.crmCompanyAdd({ 'provider': provider, 'matchIds': matchIds });
                                //     }
                                // });
                            }, (error) => {
                                this.toastr.error(getErrorMessage(error));
                            });

                            this.authWindow.close();
                        }

                        if (params.has('error')) {
                            this.toastr.info('Could Not Authenticate. Please Try Again', 'Error Authenticating');
                            window.clearInterval(this.interval);
                            this.authWindowStatusSubject.next({
                                success: false,
                                provider: provider,
                                selectedItems: null
                            } as OauthWindowStatusResult);
                            this.authWindow.close();
                        }
                    }
                } catch (e) { }
            }
        }, 1000);
    }

    open(provider: OauthProvider, matchIds, fromList?): void { // Second argument added due to DAAS-4175
        if (provider === OauthProvider.Hubspot) {
            provider = this.userService.hasAccess(AccountAttributesEnum.HUBSPOT_LITE) ? OauthProvider.HubspotLite : OauthProvider.Hubspot;
        }
        if (![OauthProvider.HubspotLite, OauthProvider.Hubspot].includes(provider)) {
            this.openFlow(provider, matchIds, fromList);
            return;
        }
        this.userService.getUserData().pipe(
            switchMap((u: User) =>
                this.accountService.getSettings(u.account_id).pipe(
                    map(response => ({ response, u })),
                    catchError((error) => {
                        if (error.status === 404) {
                            return of({ response: null, u });
                        }
                        return throwError(() => error);
                    }),
                )
            ),
            take(1),
            mergeMap(({ response, u }) => {
                if (this.hubspotService.hsCrmStatusToggledBeforeRelogin || (response?.lock_hubspot_settings && response?.hubspot_admin !== u.id)) {
                    this.openFlow(provider, matchIds, fromList);
                    return EMPTY;
                }
                return this.matDialog.open(HubspotCrmPromptComponent, { panelClass: 'modal-md' }).afterClosed().pipe(
                    map(data => ({ data, provider, matchIds, fromList }))
                );
            })
        ).subscribe(({ data, provider, matchIds, fromList }) => {
            if (data && data.success) {
                this.openFlow(provider, matchIds, fromList, data.hsCrmValue);
            } else {
                this.authWindowStatusSubject.next({
                    success: false,
                    provider: provider,
                    selectedItems: null
                } as OauthWindowStatusResult);
            }
        });
    }

    private openProviderFlow(provider, subscriber, hsCrmValue?: boolean) {
        // open the auth window
        const isProviderHubSpot = provider.toLowerCase().includes('hubspot');
        const width = 400;
        const height = isProviderHubSpot ? 580 : 500;
        const left = screen.width / 2 - 200;
        const top = isProviderHubSpot ? screen.height / 2 - 290 : screen.height / 2 - 250;
        this.authWindow = this.createWindow(this.url(provider) + '?' + this.settings(provider), provider, width, height, left, top);

        let loopCount = 300;
        let paramsHadCode: boolean = false;
        // set the interval up
        const interval = window.setInterval(() => {
            // automatically close the window after x loops
            if (loopCount-- < 0) {
                window.clearInterval(interval);
                this.authWindow.close();
                subscriber.error({
                    success: false,
                    provider,
                });
            }

            if (this.authWindow?.closed) {
                window.clearInterval(interval);
                subscriber.error({
                    success: false,
                    provider,
                });
            } else {
                try {
                    const href = this.authWindow.location.href;
                    if (href) {
                        const params = new HttpParams({
                            fromString: href.split('?')[1]
                        });

                        if (params.has('code') && !paramsHadCode) {
                            paramsHadCode = true;
                            this.authenticate(this.authWindow.name, params, hsCrmValue)
                                .pipe(
                                    switchMap(() => this.authService.updateLoggedUserDataObservable()),
                                    take(1),
                                )
                                .subscribe(r => {
                                    subscriber.next({
                                        success: true,
                                        provider,
                                    });
                                    subscriber.complete();

                                    this.authWindow.close();
                                }, (error) => {
                                    this.toastr.error(error?.error?.response);
                                    subscriber.error({
                                        success: false,
                                        provider,
                                    });

                                    this.authWindow.close();
                                });
                        }

                        if (params.has('error')) {
                            this.toastr.info('Could Not Authenticate. Please Try Again', 'Error Authenticating');
                            window.clearInterval(interval);
                            subscriber.error({
                                success: false,
                                provider,
                            });
                            this.authWindow.close();
                        }
                    }
                } catch (e) { }
            }
        }, 1000);
        const self = this;

        return {
            unsubscribe() {
                window.clearInterval(interval);
                self.authWindow.close();
            }
        };
    }

    // TODO: temporary modified copy of 'open' method, review logic (might be interceptor)
    openProvider(provider: OauthProvider): Observable<OauthWindowStatusResult> {
        return new Observable((subscriber: Subscriber<OauthWindowStatusResult>) => {
            if (provider === OauthProvider.Hubspot) {
                provider = this.userService.hasAccess(AccountAttributesEnum.HUBSPOT_LITE) ? OauthProvider.HubspotLite : OauthProvider.Hubspot;
            }
            if (![OauthProvider.HubspotLite, OauthProvider.Hubspot].includes(provider)) {
                this.openProviderFlow(provider, subscriber);
                return;
            }
            this.userService.getUserData().pipe(
                switchMap((u: User) =>
                    this.accountService.getSettings(u.account_id).pipe(
                        map(response => ({ response, u })),
                        catchError((error) => {
                            if (error.status === 404) {
                                return of({ response: null, u });
                            }
                            return throwError(() => error);
                        }),
                    ),
                ),
                take(1),
                mergeMap(({ response, u }) => {
                    if (this.hubspotService.hsCrmStatusToggledBeforeRelogin || (response?.lock_hubspot_settings && response?.hubspot_admin !== u.id)) {
                        this.openProviderFlow(provider, subscriber);
                        return EMPTY;
                    }
                    return this.matDialog.open(HubspotCrmPromptComponent, { panelClass: 'modal-md' }).afterClosed().pipe(
                        map(data => ({ data, provider, subscriber }))
                    );
                })
            ).subscribe(({ data, provider, subscriber }) => {
                if (data && data.success) {
                    this.openProviderFlow(provider, subscriber, data.hsCrmValue);
                } else {
                    subscriber.error({
                        success: false,
                        provider,
                    });
                }
            });
        });
    }

    getExportContinueEvent(): Observable<void> {
        return this.exportContinueEvent.asObservable();
    }
}
